import * as React from "react";

import { VisuallyHidden } from "@radix-ui/themes";
import { differenceInDays } from "date-fns";
import {
  HeartIcon,
  MinusIcon,
  PackageOpenIcon,
  PlusIcon,
  Share2Icon,
} from "lucide-react";

import { compactFormat, numFormat } from "@/shared.utils/number-format";
import { PrimAccordion } from "@/shared-web.ui/prim.accordion";
import { PrimCallout } from "@/shared-web.ui/prim.callout";
import { PrimDialog } from "@/shared-web.ui/prim.dialog";
import { PrimTextField } from "@/shared-web.ui/prim.text-field";
import { type RadixColor, semanticColor } from "@/shared-web.ui/styles";
import { Box } from "@/shared-web.ui/ui.box";
import { Button } from "@/shared-web.ui/ui.button";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Heading } from "@/shared-web.ui/ui.heading";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import { DeliveryTimeDisplay, StockStatus } from "@/stf.data/schema";

interface CardProps {
  children: React.ReactNode;
  className?: string;
}
const Card = (props: CardProps) => {
  const { children, className } = props;
  return (
    <Box className={cn(
      `
        rounded-1 bg-panel-solid p-3 shadow-2
        md:p-5
        sm:p-4
        xs:rounded-4
      `,
      className,
    )}
    >
      {children}
    </Box>
  );
};

interface TitleProps {
  children: React.ReactNode;
  className?: string;
}
const Title = (props: TitleProps) => {
  const { children, className } = props;
  return (
    <Heading className={className} size="6">
      {children}
    </Heading>
  );
};

interface SatisfactionProps {
  ratingDetail: {
    avgRating: number;
    ratingCount: number;
  };
  unitSold: number;
}
const Satisfaction = (props: SatisfactionProps) => {
  const { ratingDetail, unitSold } = props;

  return (
    <Text
      as="div"
      className="flex flex-row items-baseline gap-1"
      size="2"
    >
      {ratingDetail.avgRating
        ? (
            <>
              <HeartIcon
                className="size-[0.7em] text-danger-9"
              />
              <Text>
                {ratingDetail.avgRating}
              </Text>
              <Separator
                className="h-[0.7em]"
                orientation="vertical"
              />
              <Text>
                {`${compactFormat(ratingDetail.ratingCount)} Rating`}
              </Text>
              <Separator
                className="h-[0.7em]"
                orientation="vertical"
              />
            </>
          )
        : null}
      <Text>
        {`${compactFormat(unitSold)} Sold`}
      </Text>
    </Text>
  );
};

interface PriceProps {
  discountPercentage: number;
  finalPrice: number;
  stickerPrice: number;
}
const Price = (props: PriceProps) => {
  const { finalPrice, stickerPrice, discountPercentage } = props;
  const isDiscount = finalPrice < stickerPrice;

  return (
    <Flex direction="column" gap="1">
      <Text
        semanticColor={isDiscount ? "danger" : undefined}
        size="6"
        weight="medium"
      >
        {`฿${numFormat(finalPrice, { decimal: 0 })}`}
      </Text>
      <Flex align="baseline" direction="row" gap="1">
        {isDiscount && (
          <Text
            className="line-through"
            color="gray"
            size="2"
            weight="light"
          >
            {`฿${numFormat(stickerPrice, { decimal: 0 })}`}
          </Text>
        )}
        {discountPercentage
          ? (
              <Text
                size="1"
                semanticColor="danger"
                className="bg-danger-3 rounded-1 px-px"
              >
                {`-${numFormat(discountPercentage, { decimal: 0 })}%`}
              </Text>
            )
          : null}
      </Flex>
    </Flex>
  );
};

interface QtySelectorProps {
  disabled?: boolean;
  onMinus: () => void;
  onPlus: () => void;
  qty: number;
  setQty: (qty: number) => void;
}
const QtySelector = (props: QtySelectorProps) => {
  const {
    onMinus,
    onPlus,
    qty,
    setQty,
    disabled,
  } = props;

  return (
    <Flex align="center" direction="row" justify="between">
      <Text
        weight="medium"
        className={cn(
          disabled && "text-gray-9",
        )}
      >
        Qty
      </Text>
      <Flex gap="2">
        <Button
          className="size-8 p-0"
          onClick={onMinus}
          radius="full"
          variant="outline"
          disabled={disabled}
        >
          <MinusIcon className="size-4" />
        </Button>
        <input
          className={`
            w-12 text-center
            disabled:text-gray-9 disabled:bg-transparent
          `}
          onChange={(e) => {
            setQty(Number(e.target.value));
          }}
          value={qty}
          disabled={disabled}
        />
        <Button
          className="size-8 p-0"
          onClick={onPlus}
          radius="full"
          variant="outline"
          disabled={disabled}
        >
          <PlusIcon className="size-4" />
        </Button>
      </Flex>
    </Flex>
  );
};

type ReducedStockStatus = "In Stock" | "Out of Stock";

interface ReduceStockStatusProps {
  stock: StockStatus | number;
}
const reduceStockStatus = (
  { stock }: ReduceStockStatusProps,
): ReducedStockStatus => {
  if (typeof (stock) === "number") {
    if (stock === 0) {
      return "Out of Stock";
    }
    return "In Stock";
  }
  if (stock === "Out of Stock") {
    return "Out of Stock";
  }
  return "In Stock";
};

interface GetEstimatedDeliveryMessageProps {
  calDate?: Date | string;
  daysMax: number;
  daysMin: number;
}
const getEstimatedDeliveryMessage = (
  props: GetEstimatedDeliveryMessageProps,
): string => {
  const {
    daysMax, daysMin,
    calDate = new Date(),
  } = props;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateToCal: Date = typeof calDate === "string" ? new Date(calDate) : calDate;
  dateToCal.setHours(0, 0, 0, 0);

  // Calculate the estimated delivery date range
  const startDate = new Date(calDate);
  startDate.setDate(dateToCal.getDate() + daysMin);

  const endDate = new Date(calDate);
  endDate.setDate(dateToCal.getDate() + daysMax);

  const withinMinDays = differenceInDays(startDate, today);
  const withinMaxDays = differenceInDays(endDate, today);

  // Format the dates as "7-10 Mar"
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  const startStr = startDate.toLocaleDateString("en-GB", options);
  const endStr = endDate.toLocaleDateString("en-GB", options);

  return `Est. delivery by ${startStr}-${endStr}, within ${withinMinDays}-${withinMaxDays} days`;
};

interface GetStatusDisplayProps {
  deliveryTime: DeliveryTimeDisplay;
  disabled: boolean;
  stockStatus: ReducedStockStatus;
}
const getStatusDisplay = (props: GetStatusDisplayProps) => {
  const { deliveryTime, stockStatus, disabled } = props;
  const { deliveryTimeType, estimatedDeliveryDate } = deliveryTime;

  const daysMin = 0;
  const daysMax = 3;

  // define default value
  let label: string = "Unavaliable";
  let description: string = "Product is not currently available for purchase";
  let color: RadixColor = "gray";

  if (disabled) {
    // use default value (Unavaliable)
  }
  else if (stockStatus === "Out of Stock") {
    label = "Out of stock";
    description = "Product is temporarily out of stock";
  }
  else if (deliveryTimeType === "readyToShip") {
    label = "Ready to ship";
    color = semanticColor.info;
    description = getEstimatedDeliveryMessage({
      daysMin,
      daysMax,
      calDate: estimatedDeliveryDate,
    });
  }
  else if (deliveryTimeType === "withinNDays") {
    label = "Pre-order";
    color = semanticColor.warning;
    description = getEstimatedDeliveryMessage({
      daysMin,
      daysMax,
      calDate: estimatedDeliveryDate,
    });
  }
  else if (deliveryTimeType === "scheduleShipping") {
    if (estimatedDeliveryDate) {
      label = "Pre-order";
      color = semanticColor.warning;
      description = getEstimatedDeliveryMessage({
        daysMin,
        daysMax,
        calDate: estimatedDeliveryDate,
      });
    }
    else {
      // diffrent description just for debugging purpose
      description = "Product is currently unavailable for purchase";
    }
  }

  return {
    label,
    color,
    description,
  };
};

interface ShippingDetailProps {
  defaultOpen?: boolean;
  deliveryTime: DeliveryTimeDisplay;
  disabled: boolean;
  size: "1" | "2";
  stock: StockStatus | number;
}
const ShippingDetail = (props: ShippingDetailProps) => {
  const {
    stock,
    deliveryTime,
    disabled,
    size,
    defaultOpen = false,
  } = props;

  const stockStatus = reduceStockStatus({ stock });
  const {
    label, description, color,
  } = getStatusDisplay({
    deliveryTime, stockStatus, disabled,
  });

  return (
    <PrimCallout.Root
      className={cn(
        size === "1" && "px-2 py-1",
        size === "2" && "px-4 py-2",
        "flex",
      )}
      color={color}
      size="1"
      variant="surface"
    >
      <PrimAccordion.Root
        collapsible
        defaultValue={defaultOpen ? "detail" : undefined}
        type="single"
        className="w-full"
      >
        <PrimAccordion.Item className="border-none" value="detail">
          <PrimAccordion.Trigger className={`
            py-0
            hover:no-underline
          `}
          >
            <Text size={size === "1" ? "1" : "3"}>
              {label}
            </Text>
          </PrimAccordion.Trigger>
          <PrimAccordion.Content className="py-0">
            <Text size={size === "1" ? "1" : "2"}>
              {description}
            </Text>
          </PrimAccordion.Content>
        </PrimAccordion.Item>
      </PrimAccordion.Root>
    </PrimCallout.Root>
  );
};

interface CheckoutBtnProps extends Omit<React.ComponentPropsWithoutRef<typeof Button>, "children"> {}
const CheckoutBtn = React.forwardRef<
  React.ElementRef<typeof Button>,
  CheckoutBtnProps
>((props, ref) => {
  return (
    <Button
      ref={ref}
      size="4"
      {...props}
    >
      <PackageOpenIcon
        strokeWidth="2"
        className="size-5"
      />
      Add to Cart
    </Button>
  );
});
CheckoutBtn.displayName = "CheckoutBtn";

const ShareBtn = () => {
  const [copiedState, setCopiedState] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [currentUrl, setCurrentUrl] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  const onCopyClick = async () => {
    setCopiedState(true);
    await navigator.clipboard.writeText(window.location.href);
    if (inputRef.current) {
      inputRef.current.select();
    }

    setTimeout(() => {
      setCopiedState(false);
    }, 500);
  };
  return (
    <PrimDialog.Root>
      <PrimDialog.Trigger asChild>
        <IconButton
          radius="full"
          size="4"
          variant="ghost"
          screenReaders="Share"
        >
          <Share2Icon className="size-5" />
        </IconButton>
      </PrimDialog.Trigger>
      <PrimDialog.Content>
        <VisuallyHidden>
          <PrimDialog.Title>Share Product</PrimDialog.Title>
        </VisuallyHidden>
        <PrimTextField.Root
          size="3"
          value={currentUrl}
          type="text"
          ref={inputRef}
          readOnly
        >
          <PrimTextField.Slot side="right" className="pr-1">
            <Button
              size="2"
              variant={copiedState ? "solid" : "soft"}
              type="button"
              onClick={onCopyClick}
              disabled={copiedState}
            >
              {
                copiedState
                  ? "Copied"
                  : "Copy"
              }
            </Button>
          </PrimTextField.Slot>
        </PrimTextField.Root>
      </PrimDialog.Content>
    </PrimDialog.Root>
  );
};

interface SectionTitleProps {
  children: React.ReactNode;
}
const SectionTitle = (props: SectionTitleProps) => {
  const { children } = props;
  return (
    <Heading as="h2" semanticColor="accent" size="5">
      {children}
    </Heading>
  );
};

export {
  Card,
  CheckoutBtn,
  Price,
  QtySelector,
  Satisfaction,
  SectionTitle,
  ShareBtn,
  ShippingDetail,
  Title,
};
